import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function isRtlDocument() {
  if (typeof document === 'undefined') return false;
  return document.documentElement.dir === 'rtl' || getComputedStyle(document.documentElement).direction === 'rtl';
}

/** Fixed-position coords for toolbar dropdowns — renders above tables (portal). */
export function useToolbarDropdownPortal(open, align = 'start', widthMode = 'anchor', contentWidth = 210) {
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);

  const update = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const rtl = isRtlDocument();
    const margin = 8;
    const width =
      widthMode === 'content'
        ? Math.min(contentWidth, window.innerWidth - margin * 2)
        : Math.min(Math.max(rect.width, 120), window.innerWidth - margin * 2);

    const top = rect.bottom + 4;
    const anchorToEnd = align === 'end' ? !rtl : rtl;

    if (anchorToEnd) {
      let right = window.innerWidth - rect.right;
      if (right + width > window.innerWidth - margin) {
        right = margin;
      }
      setPos({ top, right, width, maxWidth: width });
    } else {
      let left = rect.left;
      if (left + width > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - width - margin);
      }
      if (left < margin) left = margin;
      setPos({ top, left, width, maxWidth: width });
    }
  }, [align, widthMode, contentWidth]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, update]);

  return { anchorRef, panelRef, pos };
}

export function ToolbarDropdownPortal({ open, panelRef, pos, className, style, children, role }) {
  if (!open || !pos) return null;
  return createPortal(
    <div
      ref={panelRef}
      role={role}
      className={className}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        right: pos.right,
        width: pos.width,
        maxWidth: pos.maxWidth ?? pos.width,
        zIndex: 200000,
        ...style,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

export function useToolbarDropdownDismiss(open, onClose, anchorRef, panelRef) {
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      const t = e.target;
      if (anchorRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, onClose, anchorRef, panelRef]);
}
